import React, { useMemo, useRef } from "react"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"
import { addDays, daysBetween, holidaysByDay } from "./plannerCalendar"
import { movable } from "./plannerItems"
import PlannerCard, { itemTitle } from "./PlannerCard"

const dayOf = date => date.toISOString().slice(0, 10)

// The Month view (ticket 15 Q16): FullCalendar's day grid on the read's days (UTC, so a day is the country's day), Monday first, only
// the month's weeks. A card per tournament (PlannerCard); a holiday is drawn on its day with every name; a card is dragged by days
// (dates only, the length kept) or opened in the quick peek; a click on an empty day starts a new proposal there (Q11). The holidays
// that give a warning (Easter, Christmas, New Year: ticket 13) say so.
export default function MonthView({ items, query, holidays, settings, onOpen, onMove, onCreate }) {
  const compact = query.density === "compact"
  const byDay = useMemo(() => holidaysByDay(holidays), [holidays])
  const events = useMemo(() => items.map(item => ({ id: item.key, title: item.label, start: item.days.start, end: addDays(item.days.end, 1), allDay: true,
    startEditable: movable(item), durationEditable: false, classNames: ["ep-month-card", `ep-card-${item.state}`, ...(item.moving ? ["is-moving"] : [])],
    extendedProps: { item } })), [items])
  // The day cells keep the handler they were mounted with: they call the latest one.
  const create = useRef(onCreate)
  create.current = onCreate
  return <div className={"ep-month" + (compact ? " is-compact" : "")}>
    <FullCalendar key={query.month} plugins={[dayGridPlugin, interactionPlugin]} initialView="dayGridMonth" initialDate={`${query.month}-01`}
      timeZone="UTC" firstDay={1} headerToolbar={false} fixedWeekCount={false} height="auto" dayMaxEventRows={compact ? 10 : 6}
      editable eventDurationEditable={false} events={events}
      eventContent={({ event }) => <>
        <span className="sr-only">{itemTitle(event.extendedProps.item, settings)}</span>
        <span aria-hidden="true"><PlannerCard item={event.extendedProps.item} settings={settings} compact={compact} /></span>
      </>}
      eventDidMount={({ el }) => el.setAttribute("role", "button")}
      eventClick={info => { info.jsEvent.preventDefault(); onOpen(info.event.extendedProps.item) }}
      eventDrop={info => {
        const item = info.event.extendedProps.item
        // The page shows the move (and may ask first): the calendar goes back to the read until it does.
        info.revert()
        onMove(item, daysBetween(item.days.start, dayOf(info.event.start)))
      }}
      dayCellClassNames={({ date }) => (byDay.has(dayOf(date)) ? ["is-holiday"] : [])}
      dayCellContent={({ date, dayNumberText }) => <span className="ep-month-day">
        <span>{dayNumberText}</span>
        {(byDay.get(dayOf(date)) || []).map(holiday => <span key={holiday.kind + holiday.name} className={"ep-holiday-name" + (holiday.warns ? " is-warning" : "")}
          title={`${holiday.name} · ${holiday.kind === "declared" ? `declared day off${holiday.source ? ` (${holiday.source})` : ""}` : "legal holiday"}`
            + (holiday.warns ? " · gives a warning" : "")}>
          <i className="bx bx-flag" aria-hidden="true" />{holiday.name}</span>)}
      </span>}
      dayCellDidMount={({ el, date }) => {
        el.plannerClick = event => { if (!event.target.closest(".fc-event, .fc-daygrid-more-link, .fc-popover")) create.current(dayOf(date)) }
        el.addEventListener("click", el.plannerClick)
      }}
      dayCellWillUnmount={({ el }) => el.removeEventListener("click", el.plannerClick)} />
  </div>
}
